import React, { useEffect, useState, useCallback, useContext } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import get from "lodash.get";

import propTypes from "../../../../constants/propTypes";
import { LoaderContext } from "../../../ui/Loader";
import AddRequirementRow from "./AddRequirementRow";
import UploadSubmittal from "./UploadSubmittal";
import Grid from "./Grid";
import {
  processDeleteRequestStatus,
  processUpdateRequestStatus,
  processCreateRequestStatus,
} from "../../../../utils/dataComponentUtils";
import { PanelWrapper, GridWrapper } from "./components";
import { mapStateToProps, mapDispatchToProps } from "./connect";
import { getTimezoneFromSpec } from "../../../../utils/timezoneUtils";
import { ShowBulkUploadSubmittalsContext } from "../ShowBulkUploadSubmittalsContext";
import {
  APPROVAL_ERROR_MESSAGE,
  getApprovalMessage,
} from "../../SpecDetail/RequirementsSection/UploadFiles/uploadFileActions";

export const dataComponentId = "SpecRequirements";
export const specsDataComponentId = "ReviewSubmittals";
export const submittalsDataComponentId = "Submittals";

export const processDeleteRequest = (
  preDataComponent,
  dataComponent,
  { setReload, showSnackNotificationAction }
) => {
  processDeleteRequestStatus(preDataComponent, dataComponent, {
    onSuccess: () => {
      setReload(specsDataComponentId, true);
      showSnackNotificationAction("The item has been deleted successfully");
    },
  });
};

export const processSaveRequest = (
  preDataComponent,
  dataComponent,
  { onHideUploadSubmittalForm, setReload, showSnackNotificationAction }
) => {
  const onSuccess = () => {
    onHideUploadSubmittalForm();
    setReload(specsDataComponentId, true);
    showSnackNotificationAction("The file has been saved successfully");
  };
  processCreateRequestStatus(preDataComponent, dataComponent, {
    onSuccess,
  });
  processUpdateRequestStatus(preDataComponent, dataComponent, {
    onSuccess,
  });
};

export const processRequests = (
  { preSubmittalsDC, submittalsDatacomponent },
  { preDataComponent, dataComponent },
  callbacks
) => {
  processDeleteRequest(preSubmittalsDC, submittalsDatacomponent, callbacks);
  processDeleteRequest(preDataComponent, dataComponent, callbacks);
  processSaveRequest(preSubmittalsDC, submittalsDatacomponent, callbacks);
  processCreateRequestStatus(preDataComponent, dataComponent, {
    onSuccess: callbacks.onRequirementCreated,
  });
};

export const resetSetAdding = (
  requirements,
  prevRequirements,
  setAdding
) => () => {
  if (requirements.length !== prevRequirements.length) {
    return setAdding(false);
  }
  const ids = requirements.map(requirement => requirement.id);
  const hasChanges = prevRequirements.some(
    requirement => !ids.includes(requirement.id)
  );
  if (hasChanges) {
    setAdding(false);
  }
};

// eslint-disable-next-line max-lines-per-function
export const RequirementsGrid = ({
  specs,
  specIds,
  dataComponent,
  submittalsDatacomponent,
  loading,
  performCreateRequest,
  performUpdateRequest,
  performDeleteRequest,
  showSnackNotificationAction,
  setReload,
  onTitleChange,
  saveSubmittal,
  setTitle,
}) => {
  const [isAdding, setAdding] = useState(false);
  const [shouldDisplayUploadForm, setDisplayUploadForm] = useState(false);
  const [currentSubmittal, setCurrentSubmittal] = useState({});
  const [preSubmittalsDC, setPrevSubmittalsDC] = useState(null);
  const [preDataComponent, setDataComponent] = useState(null);

  const { showBulkUploadSubmittals, setShowBulkUploadSubmittals } = useContext(
    ShowBulkUploadSubmittalsContext
  );

  // Uploading files takes over the panel, so a half-added requirement closes
  useEffect(() => {
    if (showBulkUploadSubmittals) setAdding(false);
  }, [showBulkUploadSubmittals]);

  useEffect(() => {
    setDisplayUploadForm(false);
    setCurrentSubmittal({});
    setShowBulkUploadSubmittals(false);
  }, [setShowBulkUploadSubmittals, specIds]);

  useEffect(() => setPrevSubmittalsDC(submittalsDatacomponent), [
    submittalsDatacomponent,
  ]);

  useEffect(() => setDataComponent(dataComponent), [dataComponent]);

  const handleRequirementCreated = useCallback(() => setAdding(false), []);

  const handleHideUploadSubmittalForm = useCallback(() => {
    onTitleChange();
    setDisplayUploadForm(false);
    setCurrentSubmittal({});
  }, [onTitleChange]);

  useEffect(
    () =>
      processRequests(
        { preSubmittalsDC, submittalsDatacomponent },
        { preDataComponent, dataComponent },
        {
          onHideUploadSubmittalForm: handleHideUploadSubmittalForm,
          onRequirementCreated: handleRequirementCreated,
          setReload,
          showSnackNotificationAction,
        }
      ),
    [
      dataComponent,
      preDataComponent,
      preSubmittalsDC,
      submittalsDatacomponent,
      setReload,
      handleHideUploadSubmittalForm,
      handleRequirementCreated,
      showSnackNotificationAction,
    ]
  );

  const hadleAddRequirementClick = useCallback(() => {
    setAdding(true);
  }, []);

  const handleRemoveRequirementRowClick = useCallback(() => {
    setAdding(false);
  }, []);

  const handleCreateRequirement = useCallback(
    ({ target: { value } }) => {
      performCreateRequest(
        dataComponentId,
        specs
          .filter(
            ({ specRequirements }) =>
              !specRequirements
                .map(({ requirementId }) => requirementId)
                .includes(value)
          )
          .map(({ id: specId }) => ({ specId, requirementId: value }))
      );
    },
    [performCreateRequest, specs]
  );

  const handleCreateSubmittal = useCallback(
    submittal => {
      const projectId = get(currentSubmittal, "specRequirement.spec.projectId");
      const specId = get(currentSubmittal, "specRequirement.specId");
      saveSubmittal(submittal, projectId, specId);
    },
    [currentSubmittal, saveSubmittal]
  );

  const handleApproveSubmittal = useCallback(
    async (id, submittalApprovedId) => {
      try {
        await performUpdateRequest(dataComponentId, id, {
          submittalApprovedId,
        });
        showSnackNotificationAction(getApprovalMessage(submittalApprovedId));
      } catch (error) {
        showSnackNotificationAction(APPROVAL_ERROR_MESSAGE);
      }
    },
    [performUpdateRequest, showSnackNotificationAction]
  );

  const handleUpdateRequirement = useCallback(
    (id, patch) => performUpdateRequest(dataComponentId, id, patch),
    [performUpdateRequest]
  );

  const handleDeleteSubmittal = useCallback(
    id => {
      performDeleteRequest(submittalsDataComponentId, id);
    },
    [performDeleteRequest]
  );

  const handleDeleteRequirement = useCallback(
    id => {
      performDeleteRequest(dataComponentId, id);
    },
    [performDeleteRequest]
  );

  const handleDisplayUploadSubmittalForm = useCallback(
    (specRequirement, submittal) => {
      onTitleChange(specRequirement);
      setAdding(false);
      setDisplayUploadForm(true);
      setCurrentSubmittal({ specRequirement, submittal });
    },
    [onTitleChange]
  );

  const handleUpdateSpec = useCallback(
    spec => performUpdateRequest(specsDataComponentId, spec.id, spec),
    [performUpdateRequest]
  );

  const disabled = isAdding;
  // The manager renders in a modal outside the page's TimezoneProvider; every
  // spec belongs to the same PO, so any of them yields the project timezone
  const timezone = getTimezoneFromSpec(specs[0]);

  return (
    <LoaderContext.Provider value={{ loading }}>
      {shouldDisplayUploadForm && (
        <UploadSubmittal
          initialValues={currentSubmittal.submittal}
          specRequirement={currentSubmittal.specRequirement}
          onSubmit={handleCreateSubmittal}
          onCancel={handleHideUploadSubmittalForm}
          dataComponentId={submittalsDataComponentId}
          timezone={timezone}
        />
      )}
      {/* Hidden rather than unmounted while the form shows, so the grid keeps
          its expanded requirements when the user comes back to it */}
      <PanelWrapper hidden={shouldDisplayUploadForm}>
        <GridWrapper>
          <Grid
            specs={specs}
            onDeleteRequirement={handleDeleteRequirement}
            onDisplayUploadSubmittalForm={handleDisplayUploadSubmittalForm}
            onApproveSubmittal={handleApproveSubmittal}
            onUpdateRequirement={handleUpdateRequirement}
            onDeleteSubmittal={handleDeleteSubmittal}
            onAddRequirementClick={hadleAddRequirementClick}
            onUpdateSpec={handleUpdateSpec}
            setTitle={setTitle}
            onTitleChange={onTitleChange}
            setReload={setReload}
            disabled={disabled}
          />
        </GridWrapper>
        <AddRequirementRow
          isActive={isAdding}
          onRemove={handleRemoveRequirementRowClick}
          onCreate={handleCreateRequirement}
        />
      </PanelWrapper>
    </LoaderContext.Provider>
  );
};

RequirementsGrid.propTypes = {
  specs: PropTypes.arrayOf(propTypes.spec).isRequired,
  dataComponent: propTypes.dataComponent,
  submittalsDatacomponent: propTypes.dataComponent,
  loading: PropTypes.bool,
  performCreateRequest: PropTypes.func.isRequired,
  performUpdateRequest: PropTypes.func.isRequired,
  performDeleteRequest: PropTypes.func.isRequired,
  showSnackNotificationAction: PropTypes.func.isRequired,
  onTitleChange: PropTypes.func.isRequired,
  setReload: PropTypes.func.isRequired,
  saveSubmittal: PropTypes.func.isRequired,
  setTitle: PropTypes.func.isRequired,
  specIds: PropTypes.arrayOf(PropTypes.string).isRequired,
};

RequirementsGrid.defaultProps = {
  requirements: [],
};

export default connect(mapStateToProps, mapDispatchToProps)(RequirementsGrid);
